import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../globals';
import { useAsyncStatus, useDebouncedCallback, useToggle } from '../../hooks';
import * as api from '../../redux/api';
import InternalToolsModule from '../../redux/internal_tooling';
import Button, { BUTTON_TYPE, ButtonGroup } from '../generic/Button';
import SelectableReactTable from '../generic/SelectableReactTable';
import Text from '../generic/Text';
import ModalAreYouSure from './ModalAreYouSure';
import OrganizationSearch from './OrganizationCard/OrganizationSearch';
import type { FailedFetchResponse } from '../../helpers/errors';

type UserCardUser = {
  id: number;
  email: string;
  username: string;
  organization: {
    fetcherConfig: unknown;
    title: string;
    id: number;
  }
};

const UserCard: VoidFunctionComponent = () => {
  const [filter, setFilter] = useState('');
  const [users, setUsers] = useState<UserCardUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserCardUser | null>(null);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();
  const [isDeleteModalShowing, onToggleDeleteModal] = useToggle();
  const [isChangeUserOrgModalShowing, onToggleChangeUserOrg] = useToggle();

  const selectedOrg = useSelector(InternalToolsModule.selectors.getSelectedOrg);

  const fetchUsersByEmail = useDebouncedCallback(async (email: string) => {
    onPending();

    try {
      const { data } = await api.get<UserCardUser[]>('/api/internal-tools/users', false, { email });
      onSuccess();

      setUsers(data ?? []);
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, 500);

  // Fetch new data when the filter changes.
  useEffect(() => {
    fetchUsersByEmail(filter);
    // We don't want to include the fetchUsersByEmail function because it'll infinitely fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const deleteSelectedUser = useCallback(async () => {
    if (selectedUser === null) { return; }

    await api.remove(`/api/internal-tools/user/${selectedUser.id}`);
    fetchUsersByEmail(filter);
    // We don't want to include the fetchUsersByEmail function because it'll infinitely fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, filter]);

  const updatedSelectedUser = useCallback(async () => {
    if (selectedUser === null) { return; }

    await api.put(`/api/internal-tools/user/${selectedUser.id}`, { organizationId: selectedOrg.id });
    fetchUsersByEmail(filter);
    // We don't want to include the fetchUsersByEmail function because it'll infinitely fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedOrg, filter]);

  const changeToOrg = useCallback(async () => {
    if (!selectedUser) {
      return;
    }

    onPending();
    try {
      await api.put(`/api/users/${window.user?.id}`, { organizationId: selectedUser?.organization.id });
      onSuccess();
      window.location.reload();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [selectedUser, onError, onPending, onSuccess]);

  return (
    <div className="card">
      <h3 className="card-title">User</h3>
      <div className="internal-tool-component">
        <input
          type="text"
          placeholder="Filter by Email"
          onChange={(event) => {
            const textFilter = event.target.value;
            setFilter(textFilter);
          }}
          style={{ flexGrow: 20 }}
        />
        <SelectableReactTable<UserCardUser | null>
          data={users}
          loading={status.loading}
          loadingText="fetching users"
          showPagination
          showPaginationTop
          showPaginationBottom={false}
          defaultPageSize={10}
          className="-striped -highlight"
          columns={[
            {
              Header: 'Email',
              accessor: 'email',
            },
            {
              Header: 'Username',
              accessor: 'username',
            },
            {
              Header: 'org',
              accessor: 'organization.title',
            },
          ]}
          selectedData={selectedUser}
          onSelect={(user) => {
            setSelectedUser(user);
          }}
          areEqual={(userA: UserCardUser | null, userB: UserCardUser | null) =>
            userA?.id === userB?.id}
          selectedBackgroundColor="#00afec"
          selectedTextColor="black"
        />
        {selectedUser && (
          <>
            <div>
              <Text.H2>Actions</Text.H2>
              <ButtonGroup>
                {selectedUser?.organization && (
                  <Button type={BUTTON_TYPE.SLATE} onClick={changeToOrg}>
                    Change to User&apos;s Org ({selectedUser.organization.title})
                  </Button>
                )}
                <Button type={BUTTON_TYPE.ERROR} onClick={onToggleDeleteModal}>
                  Delete {selectedUser.email}
                </Button>
                <Button type={BUTTON_TYPE.ERROR} onClick={onToggleChangeUserOrg}>
                  Change orgs for {selectedUser.email}
                </Button>
              </ButtonGroup>
            </div>
            <ModalAreYouSure
              executeAction={deleteSelectedUser}
              actionName={`Delete ${selectedUser.email}`}
              title={`Deleting User ${selectedUser.email}`}
              show={isDeleteModalShowing}
              onHide={onToggleDeleteModal}
              verificationString={selectedUser.email}
            />
            <ModalAreYouSure
              executeAction={updatedSelectedUser}
              actionName={`Change Orgs for ${selectedUser.email}`}
              title={`Change orgs for user ${selectedUser.email}`}
              verificationString={selectedUser.email}
              onHide={onToggleChangeUserOrg}
              show={isChangeUserOrgModalShowing}
              reversible
            >
              <h3>Select {selectedUser.email}&apos;s new org</h3>
              <OrganizationSearch searchOnly />
            </ModalAreYouSure>
          </>
        )}
      </div>
    </div>
  );
};

export default UserCard;
